import React from 'react';
import clsx from 'clsx';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Image from '@/elements/Image/Image.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import { IProps } from './FiftyFiftyImageLinksBlock.interface';
import './FiftyFiftyImageLinksBlock.styles.css';

export const FiftyFiftyImageLinksBlock: React.FC<IProps> = ({
    className,
    heading,
    text,
    buttons,
    image,
    caption,
    mediaPosition = 'right', // Default to image on the right
    ...blockWrapperProps
}) => {
    const imageColOrder = clsx({
        'order-2': true, // Image is always second on mobile
        'md:order-1': mediaPosition === 'left',
        'md:order-2': mediaPosition === 'right',
    });

    const contentColOrder = clsx({
        'order-1': true, // Content (text+links) is always first on mobile
        'md:order-2': mediaPosition === 'left',
        'md:order-1': mediaPosition === 'right',
    });

    return (
        <BlockWrapper
            className={clsx('fiftyFiftyImageLinksBlock', className)}
            {...blockWrapperProps}
        >
            <ContentWrapper>
                {/* Content Column (Heading, Text, Buttons) */}
                <ContentColumn
                    className={clsx('fiftyFiftyImageLinksBlock__contentColumn', contentColOrder)}
                >
                    {heading && (
                        <Heading
                            level={2}
                            className="fiftyFiftyImageLinksBlock__heading"
                            html={heading}
                        />
                    )}
                    {text && <Text className="fiftyFiftyImageLinksBlock__text" html={text} />}
                    {buttons && buttons.length > 0 && (
                        <div className="fiftyFiftyImageLinksBlock__buttons">
                            {buttons.map((buttonProps, index) => {
                                // Cast the incoming props to AnchorProps as we know we are rendering anchors
                                const { href, ...restButtonProps } = buttonProps as import('@/elements/Button/Button.interface').AnchorProps;

                                const anchorProps: import('@/elements/Button/Button.interface').AnchorProps = {
                                    ...restButtonProps,
                                    as: 'a',
                                    href: href || '#', // Ensure href is always provided for anchors
                                };
                                return (
                                    <Button
                                        key={`imagelinks-block-button-${index}`}
                                        {...anchorProps} // Spread the correctly typed props
                                        className={clsx('fiftyFiftyImageLinksBlock__button', buttonProps.className)}
                                        variant='secondary'
                                        icon='arrow-right'
                                        iconPosition='after'
                                    />
                                );
                            })}
                        </div>
                    )}
                </ContentColumn>

                {/* Image Column */}
                <ContentColumn
                    className={clsx('fiftyFiftyImageLinksBlock__imageColumn', imageColOrder)}
                >
                    {image && <Image {...image} className="fiftyFiftyImageLinksBlock__image" rounded={true} />}
                    {caption && (
                        <Text className="fiftyFiftyImageLinksBlock__caption" html={caption} />
                    )}
                </ContentColumn>
            </ContentWrapper>
        </BlockWrapper>
    );
};

export default FiftyFiftyImageLinksBlock;
